/**
 * Service de journalisation des accès pour l'application web
 * Permet d'enregistrer et de récupérer l'historique des connexions
 */

import { supabase } from '../supabaseClient';

// Types pour la journalisation
export enum AccessType {
  LOGIN = 'login',
  LOGOUT = 'logout',
  DATA_ACCESS = 'data_access',
  DATA_MODIFICATION = 'data_modification',
  EXPORT = 'export',
  IMPORT = 'import',
  SETTINGS_CHANGE = 'settings_change',
  PASSWORD_CHANGE = 'password_change',
  ACCOUNT_DELETION = 'account_deletion'
}

export interface AccessLogEntry {
  id: string;
  user_id: string;
  timestamp: string;
  access_type: AccessType;
  description: string;
  device_info: {
    device_type: string;
    os: string;
    os_version: string;
    app_version: string;
    user_agent?: string;
    browser?: string;
    browser_version?: string;
  };
  ip_address?: string;
  location?: {
    latitude?: number;
    longitude?: number;
  };
  created_at: string;
}

/**
 * Récupère les informations du navigateur
 */
function getBrowserInfo() {
  const userAgent = navigator.userAgent;
  
  // Détecter le navigateur
  let browser = 'Unknown';
  let browserVersion = 'Unknown';
  
  if (userAgent.indexOf('Chrome') > -1 && userAgent.indexOf('Edg') === -1) {
    browser = 'Chrome';
    const match = userAgent.match(/Chrome\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  } else if (userAgent.indexOf('Firefox') > -1) {
    browser = 'Firefox';
    const match = userAgent.match(/Firefox\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  } else if (userAgent.indexOf('Safari') > -1 && userAgent.indexOf('Chrome') === -1) {
    browser = 'Safari';
    const match = userAgent.match(/Version\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  } else if (userAgent.indexOf('Edg') > -1) {
    browser = 'Edge';
    const match = userAgent.match(/Edg\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  }
  
  // Détecter l'OS
  let os = 'Unknown';
  let osVersion = 'Unknown';
  
  if (userAgent.indexOf('Windows') > -1) {
    os = 'Windows';
    const match = userAgent.match(/Windows NT (\d+\.\d+)/);
    osVersion = match ? match[1] : 'Unknown';
  } else if (userAgent.indexOf('Mac OS X') > -1) {
    os = 'macOS';
    const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
    osVersion = match ? match[1].replace('_', '.') : 'Unknown';
  } else if (userAgent.indexOf('Linux') > -1) {
    os = 'Linux';
    osVersion = 'Unknown';
  } else if (userAgent.indexOf('Android') > -1) {
    os = 'Android';
    const match = userAgent.match(/Android (\d+\.\d+)/);
    osVersion = match ? match[1] : 'Unknown';
  } else if (userAgent.indexOf('iOS') > -1 || userAgent.indexOf('iPhone') > -1 || userAgent.indexOf('iPad') > -1) {
    os = 'iOS';
    const match = userAgent.match(/OS (\d+[._]\d+)/);
    osVersion = match ? match[1].replace('_', '.') : 'Unknown';
  }
  
  return {
    device_type: 'web',
    os,
    os_version: osVersion,
    app_version: '1.0.0', // Version de l'app web
    user_agent: userAgent,
    browser,
    browser_version: browserVersion,
  };
}

/**
 * Récupère l'adresse IP (via un service externe)
 */
async function getIpAddress(): Promise<string | undefined> {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.warn('Impossible de récupérer l\'adresse IP:', error);
    return undefined;
  }
}

/**
 * Journalise un accès ou une action
 * @param accessType Type d'accès ou d'action
 * @param description Description de l'accès ou de l'action
 */
export async function logAccess(accessType: AccessType, description: string): Promise<void> {
  try {
    // Récupérer l'utilisateur actuel
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.warn('Tentative de journalisation sans utilisateur connecté');
      return;
    }
    
    // Récupérer les informations sur le navigateur
    const deviceInfo = getBrowserInfo();
    
    // Récupérer l'adresse IP
    const ipAddress = await getIpAddress();
    
    // Créer l'entrée de journal
    const logEntry: Omit<AccessLogEntry, 'id' | 'created_at'> = {
      user_id: user.id,
      timestamp: new Date().toISOString(),
      access_type: accessType,
      description,
      device_info: deviceInfo,
      ip_address: ipAddress,
    };
    
    // Insérer dans la table des logs
    const { error } = await supabase.from('access_logs').insert(logEntry);
    
    if (error) {
      console.error('Erreur lors de la journalisation:', error);
    }
    
  } catch (error) {
    console.error('Erreur lors de la journalisation:', error);
    // Ne pas bloquer l'application en cas d'erreur de journalisation
  }
}

/**
 * Récupère l'historique des accès pour l'utilisateur actuel
 * @param limit Nombre maximum d'entrées à récupérer
 * @param offset Offset pour la pagination
 * @returns Liste des accès
 */
export async function getAccessHistory(limit = 50, offset = 0): Promise<AccessLogEntry[]> {
  try {
    // Récupérer l'utilisateur actuel
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Utilisateur non connecté');
    }
    
    // Récupérer l'historique des accès
    const { data, error } = await supabase
      .from('access_logs')
      .select('*')
      .eq('user_id', user.id)
      .order('timestamp', { ascending: false })
      .range(offset, offset + limit - 1);
    
    if (error) {
      throw error;
    }
    
    return (data || []) as AccessLogEntry[];
    
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'historique des accès:', error);
    return [];
  }
}

